import { useState, useCallback, useRef } from "react";
import { useSources } from "../hooks/useSources";
import { createSource, updateSource, deleteSource, uploadSourceLogo, deleteSourceLogo } from "../api/sources";
import { apiFetch, resolveMediaUrl } from "../api/client";
import type { SourceEntry } from "../types";

interface AutoDiscoverResult {
  item_selector: string;
  title_selector: string;
  link_selector: string;
  date_selector: string | null;
  content_selector: string | null;
}

interface ScraperConfig {
  url: string;
  item_selector: string;
  title_selector: string;
  link_selector: string;
  date_selector: string;
  content_selector: string;
  author: string;
}

const EMPTY_CONFIG: ScraperConfig = {
  url: "",
  item_selector: "",
  title_selector: "",
  link_selector: "",
  date_selector: "",
  content_selector: "",
  author: "",
};

function parseConfig(source: SourceEntry): ScraperConfig {
  try {
    const config = source.config_json ? JSON.parse(source.config_json) : {};
    return {
      url: config.url || "",
      item_selector: config.item_selector || "",
      title_selector: config.title_selector || "",
      link_selector: config.link_selector || "",
      date_selector: config.date_selector || "",
      content_selector: config.content_selector || "",
      author: config.author || "",
    };
  } catch {
    return { ...EMPTY_CONFIG };
  }
}

function configToJson(config: ScraperConfig): string {
  const obj: Record<string, string> = { url: config.url, item_selector: config.item_selector, title_selector: config.title_selector };
  if (config.link_selector) obj.link_selector = config.link_selector;
  if (config.date_selector) obj.date_selector = config.date_selector;
  if (config.content_selector) obj.content_selector = config.content_selector;
  if (config.author) obj.author = config.author;
  return JSON.stringify(obj);
}

export function WebScraperView() {
  const { sources, loading, refresh } = useSources();
  const scraperSources = sources.filter((s) => s.type === "web_scraper");

  // Neuen Scraper anlegen
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newConfig, setNewConfig] = useState<ScraperConfig>({ ...EMPTY_CONFIG });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Editieren
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editConfig, setEditConfig] = useState<ScraperConfig>({ ...EMPTY_CONFIG });

  // Auto-Discover
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState("");
  const [discoverSuccess, setDiscoverSuccess] = useState(false);
  const [discoverSlow, setDiscoverSlow] = useState(false);

  // Auto-Discover für Edit-Modus
  const [discoveringEdit, setDiscoveringEdit] = useState(false);
  const [discoverEditError, setDiscoverEditError] = useState("");
  const [discoverEditSuccess, setDiscoverEditSuccess] = useState(false);
  const [discoverEditSlow, setDiscoverEditSlow] = useState(false);

  // Logo Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUploadId, setLogoUploadId] = useState<number | null>(null);

  const handleAdd = useCallback(async () => {
    if (!newConfig.url.trim()) {
      setError("Bitte eine URL eingeben.");
      return;
    }
    try {
      new URL(newConfig.url.trim());
    } catch {
      setError("Bitte eine gültige URL eingeben (z.B. https://...)");
      return;
    }
    if (!newConfig.item_selector.trim()) {
      setError("Bitte einen Item-Selektor eingeben (z.B. article.news-item)");
      return;
    }
    if (!newConfig.title_selector.trim()) {
      setError("Bitte einen Titel-Selektor eingeben (z.B. h2 a)");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createSource({
        type: "web_scraper",
        name: newName.trim() || new URL(newConfig.url.trim()).hostname,
        config_json: configToJson(newConfig),
        enabled: true,
      });
      setNewName("");
      setNewConfig({ ...EMPTY_CONFIG });
      setShowAdd(false);
      refresh();
    } catch (e: any) {
      setError(e.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }, [newName, newConfig, refresh]);

  const handleDelete = useCallback(async (source: SourceEntry) => {
    if (!confirm(`"${source.name}" wirklich löschen?`)) return;
    try {
      await deleteSource(source.id);
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [refresh]);

  const handleToggle = useCallback(async (source: SourceEntry) => {
    try {
      await updateSource(source.id, { enabled: !source.enabled });
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [refresh]);

  const startEdit = useCallback((source: SourceEntry) => {
    setEditId(source.id);
    setEditName(source.name);
    setEditConfig(parseConfig(source));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (editId === null) return;
    try {
      await updateSource(editId, {
        name: editName.trim(),
        config_json: configToJson(editConfig),
      });
      setEditId(null);
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [editId, editName, editConfig, refresh]);

  // ─── Auto-Discover: Neue Quelle ───
  const handleAutoDiscover = useCallback(async () => {
    const url = newConfig.url.trim();
    if (!url) {
      setDiscoverError("Bitte zuerst eine URL eingeben.");
      return;
    }
    try { new URL(url); } catch { setDiscoverError("Bitte eine gültige URL eingeben."); return; }

    setDiscovering(true);
    setDiscoverError("");
    setDiscoverSuccess(false);
    setDiscoverSlow(false);

    // Nach 10s Hinweis zeigen, dass es dauern kann
    const slowTimer = setTimeout(() => setDiscoverSlow(true), 10000);
    // AbortController für 200s Frontend-Timeout
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 200000);

    try {
      const result = await apiFetch<AutoDiscoverResult>("/scraper/auto-discover", {
        method: "POST",
        body: JSON.stringify({ url }),
        signal: controller.signal,
      });
      setNewConfig((c) => ({
        ...c,
        item_selector: result.item_selector || c.item_selector,
        title_selector: result.title_selector || c.title_selector,
        link_selector: result.link_selector || c.link_selector,
        date_selector: result.date_selector || c.date_selector,
        content_selector: result.content_selector || c.content_selector,
      }));
      setDiscoverSuccess(true);
      setTimeout(() => setDiscoverSuccess(false), 4000);
    } catch (e: any) {
      if (e.name === "AbortError") {
        setDiscoverError("Timeout: Die Analyse hat zu lange gedauert. Bitte erneut versuchen.");
      } else {
        setDiscoverError(e.message || "Auto-Detect fehlgeschlagen");
      }
    } finally {
      clearTimeout(slowTimer);
      clearTimeout(abortTimer);
      setDiscovering(false);
      setDiscoverSlow(false);
    }
  }, [newConfig.url]);

  // ─── Auto-Discover: Edit-Modus ───
  const handleAutoDiscoverEdit = useCallback(async () => {
    const url = editConfig.url.trim();
    if (!url) { setDiscoverEditError("Bitte zuerst eine URL eingeben."); return; }
    try { new URL(url); } catch { setDiscoverEditError("Bitte eine gültige URL eingeben."); return; }

    setDiscoveringEdit(true);
    setDiscoverEditError("");
    setDiscoverEditSuccess(false);
    setDiscoverEditSlow(false);

    const slowTimer = setTimeout(() => setDiscoverEditSlow(true), 10000);
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 200000);

    try {
      const result = await apiFetch<AutoDiscoverResult>("/scraper/auto-discover", {
        method: "POST",
        body: JSON.stringify({ url }),
        signal: controller.signal,
      });
      setEditConfig((c) => ({
        ...c,
        item_selector: result.item_selector || c.item_selector,
        title_selector: result.title_selector || c.title_selector,
        link_selector: result.link_selector || c.link_selector,
        date_selector: result.date_selector || c.date_selector,
        content_selector: result.content_selector || c.content_selector,
      }));
      setDiscoverEditSuccess(true);
      setTimeout(() => setDiscoverEditSuccess(false), 4000);
    } catch (e: any) {
      if (e.name === "AbortError") {
        setDiscoverEditError("Timeout: Die Analyse hat zu lange gedauert. Bitte erneut versuchen.");
      } else {
        setDiscoverEditError(e.message || "Auto-Detect fehlgeschlagen");
      }
    } finally {
      clearTimeout(slowTimer);
      clearTimeout(abortTimer);
      setDiscoveringEdit(false);
      setDiscoverEditSlow(false);
    }
  }, [editConfig.url]);

  const handleLogoClick = useCallback((sourceId: number) => {
    setLogoUploadId(sourceId);
    fileInputRef.current?.click();
  }, []);

  const handleLogoFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || logoUploadId === null) return;
    try {
      await uploadSourceLogo(logoUploadId, file);
      refresh();
    } catch (err: any) {
      alert("Logo-Upload fehlgeschlagen: " + (err.message || "Unbekannt"));
    } finally {
      setLogoUploadId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [logoUploadId, refresh]);

  const handleLogoDelete = useCallback(async (sourceId: number) => {
    try {
      await deleteSourceLogo(sourceId);
      refresh();
    } catch (err: any) {
      alert("Fehler: " + (err.message || "Unbekannt"));
    }
  }, [refresh]);

  const renderSelectorField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    required?: boolean,
  ) => (
    <div className="scraper-field">
      <label>
        {label} {required && <span style={{ color: "var(--danger)" }}>*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="scraper-input"
      />
    </div>
  );

  const renderSourceCard = (source: SourceEntry) => {
    const config = parseConfig(source);
    return (
      <div key={source.id} className={`feed-card ${!source.enabled ? "feed-card-disabled" : ""}`}>
        {editId === source.id ? (
          /* Edit-Modus */
          <div className="feed-card-edit" style={{ width: "100%" }}>
            <div className="scraper-field">
              <label>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
              />
            </div>
            {/* URL + Auto-Discover (Edit) */}
            <div className="scraper-field" style={{ marginTop: 8 }}>
              <label>Seiten-URL <span style={{ color: "var(--danger)" }}>*</span></label>
              <div className="scraper-url-row">
                <input
                  type="text"
                  value={editConfig.url}
                  onChange={(e) => setEditConfig((c) => ({ ...c, url: e.target.value }))}
                  placeholder="https://www.enisa.europa.eu/news"
                  className="scraper-input"
                />
                <button
                  className={`btn-auto-discover ${discoverEditSuccess ? "btn-auto-discover-success" : ""}`}
                  onClick={handleAutoDiscoverEdit}
                  disabled={discoveringEdit || !editConfig.url.trim()}
                  title="CSS-Selektoren automatisch per KI erkennen"
                >
                  {discoveringEdit ? (
                    <><span className="spinner-sm" /> {discoverEditSlow ? "KI analysiert..." : "Analysiere..."}</>
                  ) : discoverEditSuccess ? (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg> Erkannt!</>
                  ) : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> Auto-Detect</>
                  )}
                </button>
              </div>
              {discoverEditSlow && discoveringEdit && (
                <p className="auto-discover-slow-hint">Die KI-Analyse kann bei lokalen Modellen bis zu 3 Minuten dauern. Bitte warten...</p>
              )}
              {discoverEditError && <p className="feed-form-error" style={{ marginTop: 6 }}>{discoverEditError}</p>}
              {discoverEditSuccess && <p className="auto-discover-hint">Selektoren wurden automatisch erkannt und eingetragen.</p>}
            </div>
            <div className="scraper-grid" style={{ marginTop: 8 }}>
              {renderSelectorField("Item-Selektor", editConfig.item_selector, (v) => setEditConfig((c) => ({ ...c, item_selector: v })), "article.tileItem", true)}
              {renderSelectorField("Titel-Selektor", editConfig.title_selector, (v) => setEditConfig((c) => ({ ...c, title_selector: v })), "h2 a", true)}
              {renderSelectorField("Link-Selektor", editConfig.link_selector, (v) => setEditConfig((c) => ({ ...c, link_selector: v })), "h2 a (optional, Default = Titel)")}
              {renderSelectorField("Datum-Selektor", editConfig.date_selector, (v) => setEditConfig((c) => ({ ...c, date_selector: v })), ".date (optional)")}
              {renderSelectorField("Content-Selektor", editConfig.content_selector, (v) => setEditConfig((c) => ({ ...c, content_selector: v })), ".intro-text (optional)")}
              {renderSelectorField("Autor", editConfig.author, (v) => setEditConfig((c) => ({ ...c, author: v })), "ENISA (optional, statisch)")}
            </div>
            <div className="feed-form-actions">
              <button className="btn-primary btn-sm" onClick={handleSaveEdit}>Speichern</button>
              <button className="btn-ghost btn-sm" onClick={() => setEditId(null)}>Abbrechen</button>
            </div>
          </div>
        ) : (
          /* Anzeige-Modus */
          <>
            {/* Logo-Bereich */}
            <div className="source-logo-area">
              {source.logo_url ? (
                <div className="source-logo-preview">
                  <img src={resolveMediaUrl(source.logo_url!)} alt={`${source.name} Logo`} />
                </div>
              ) : (
                <div className="source-logo-placeholder">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                </div>
              )}
              <div className="source-logo-actions">
                <button className="btn-ghost btn-xs" onClick={() => handleLogoClick(source.id)}>
                  {source.logo_url ? "Ändern" : "Logo"}
                </button>
                {source.logo_url && (
                  <button className="btn-ghost btn-xs" onClick={() => handleLogoDelete(source.id)}>
                    Entfernen
                  </button>
                )}
              </div>
            </div>

            <div className="feed-card-info">
              <div className="feed-card-header">
                <span className={`feed-status-dot ${source.enabled ? "dot-active" : "dot-inactive"}`} />
                <h3 className="feed-card-name">{source.name}</h3>
                <span className="feed-type-badge">Scraper</span>
              </div>
              <p className="feed-card-url">{config.url}</p>
              <div className="scraper-selectors-preview">
                <span title="Item-Selektor">{config.item_selector}</span>
                <span title="Titel-Selektor">{config.title_selector}</span>
                {config.date_selector && <span title="Datum-Selektor">{config.date_selector}</span>}
              </div>
              <span className="feed-card-date">
                Erstellt: {new Date(source.created_at).toLocaleDateString("de-DE")}
              </span>
            </div>
            <div className="feed-card-actions">
              <button
                className={`btn-sm ${source.enabled ? "btn-ghost" : "btn-primary"}`}
                onClick={() => handleToggle(source)}
              >
                {source.enabled ? "Pausieren" : "Aktivieren"}
              </button>
              <button className="btn-ghost btn-sm" onClick={() => startEdit(source)}>
                Bearbeiten
              </button>
              <button className="btn-danger btn-sm" onClick={() => handleDelete(source)}>
                Löschen
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Hidden File Input für Logo-Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
        style={{ display: "none" }}
        onChange={handleLogoFile}
      />

      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">Web-Scraper</h2>
          <p className="text-muted">
            Scrapt News-Einträge direkt von Webseiten per CSS-Selektoren.
            Ideal für Seiten ohne RSS-Feed (z.B. ENISA, BSI).
          </p>
        </div>
        <button className="btn-primary" onClick={() => { setShowAdd(true); setError(""); }}>
          + Scraper hinzufügen
        </button>
      </div>

      {/* Neuen Scraper anlegen */}
      {showAdd && (
        <div className="feed-add-card">
          <h3>Neuen Web-Scraper hinzufügen</h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            Gib die URL ein und klicke auf <strong>Auto-Detect</strong>, um die CSS-Selektoren automatisch per KI erkennen zu lassen.
            Alternativ kannst du die Selektoren manuell über die Browser-DevTools (F12) ermitteln.
          </p>

          <div className="scraper-field" style={{ marginBottom: 12 }}>
            <label>Name (optional)</label>
            <input
              type="text"
              placeholder="Wird aus der Domain übernommen"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>

          {/* URL + Auto-Discover */}
          <div className="scraper-field" style={{ marginBottom: 12 }}>
            <label>
              Seiten-URL <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <div className="scraper-url-row">
              <input
                type="text"
                value={newConfig.url}
                onChange={(e) => setNewConfig((c) => ({ ...c, url: e.target.value }))}
                placeholder="https://www.enisa.europa.eu/news"
                className="scraper-input"
              />
              <button
                className={`btn-auto-discover ${discoverSuccess ? "btn-auto-discover-success" : ""}`}
                onClick={handleAutoDiscover}
                disabled={discovering || !newConfig.url.trim()}
                title="CSS-Selektoren automatisch per KI erkennen"
              >
                {discovering ? (
                  <><span className="spinner-sm" /> {discoverSlow ? "KI analysiert..." : "Analysiere..."}</>
                ) : discoverSuccess ? (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg> Erkannt!</>
                ) : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> Auto-Detect</>
                )}
              </button>
            </div>
            {discoverSlow && discovering && (
              <p className="auto-discover-slow-hint">Die KI-Analyse kann bei lokalen Modellen bis zu 3 Minuten dauern. Bitte warten...</p>
            )}
            {discoverError && <p className="feed-form-error" style={{ marginTop: 6 }}>{discoverError}</p>}
            {discoverSuccess && <p className="auto-discover-hint">Selektoren wurden automatisch erkannt und eingetragen.</p>}
          </div>

          <div className="scraper-grid">
            {renderSelectorField("Item-Selektor", newConfig.item_selector, (v) => setNewConfig((c) => ({ ...c, item_selector: v })), "article.tileItem — CSS-Selektor für jeden News-Eintrag", true)}
            {renderSelectorField("Titel-Selektor", newConfig.title_selector, (v) => setNewConfig((c) => ({ ...c, title_selector: v })), "h2 a — innerhalb des Items", true)}
            {renderSelectorField("Link-Selektor", newConfig.link_selector, (v) => setNewConfig((c) => ({ ...c, link_selector: v })), "h2 a — Default = Titel-Selektor (optional)")}
            {renderSelectorField("Datum-Selektor", newConfig.date_selector, (v) => setNewConfig((c) => ({ ...c, date_selector: v })), ".date — innerhalb des Items (optional)")}
            {renderSelectorField("Content-Selektor", newConfig.content_selector, (v) => setNewConfig((c) => ({ ...c, content_selector: v })), ".intro-text — Teaser/Intro (optional)")}
            {renderSelectorField("Autor", newConfig.author, (v) => setNewConfig((c) => ({ ...c, author: v })), "ENISA — statischer Autor für alle Einträge (optional)")}
          </div>

          {error && <p className="feed-form-error">{error}</p>}
          <div className="feed-form-actions">
            <button className="btn-primary" onClick={handleAdd} disabled={saving}>
              {saving ? "Wird gespeichert..." : "Hinzufügen"}
            </button>
            <button className="btn-ghost" onClick={() => setShowAdd(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Quellen-Liste */}
      {loading ? (
        <p className="loading-text">Lade Quellen...</p>
      ) : scraperSources.length === 0 && !showAdd ? (
        <div className="feed-empty">
          <div className="feed-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
            </svg>
          </div>
          <p>Noch keine Web-Scraper konfiguriert.</p>
          <button className="btn-primary" onClick={() => setShowAdd(true)}>
            Ersten Scraper hinzufügen
          </button>
        </div>
      ) : (
        <div className="feed-list">
          {scraperSources.map((s) => renderSourceCard(s))}
        </div>
      )}
    </div>
  );
}
